import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Work } from "@/types";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";
import PageHeader from "@/components/base/PageHeader";
import WorkCard from "@/components/base/WorkCard";
import { works as rawWorks } from "@/mocks/works";

export default function WorkDetail() {
  const { id } = useParams();
  const works = rawWorks as unknown as Work[];
  const work = works.find((w) => w.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useDocumentMeta({
    title: work ? `${work.title} - 舒淇影视作品详情` : "作品详情 - 舒淇影视作品",
    description: work
      ? `${work.title}（${work.year}）是舒淇主演的${work.genre}${work.type}，导演${work.director}，舒淇饰演${work.role}。${work.synopsis.slice(0, 60)}`
      : "舒淇影视作品详情页。",
    keywords: work
      ? `${work.title},舒淇${work.title},舒淇影视作品,舒淇${work.genre}`
      : "舒淇影视作品",
  });

  const related = useMemo(() => {
    if (!work) return [];
    return works
      .filter(
        (w) =>
          w.id !== work.id &&
          (w.genre === work.genre || Math.abs(w.year - work.year) <= 3)
      )
      .slice(0, 4);
  }, [works, work]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  if (!work) {
    return (
      <div>
        <PageHeader
          title="未找到该作品"
          breadcrumb={[{ label: "首页", path: "/" }, { label: "影视作品", path: "/works" }]}
        />
        <div className="max-w-content mx-auto px-4 md:px-8 py-24 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-secondary-400">
            <i className="ri-error-warning-line text-3xl" />
          </div>
          <p className="mt-5 text-foreground-600">
            抱歉，我们找不到这部作品，它可能已被移除。
          </p>
          <Link
            to="/works"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            返回作品列表
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(work.id);

  return (
    <div>
      <PageHeader
        eyebrow="Work Detail"
        title={work.title}
        breadcrumb={[
          { label: "首页", path: "/" },
          { label: "影视作品", path: "/works" },
          { label: work.title },
        ]}
      />

      <section className="max-w-content mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-1">
            <div className="w-full max-w-sm mx-auto lg:mx-0 aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-200">
              <img
                src={work.poster}
                alt={`${work.title} 舒淇影视作品封面`}
                title={`${work.title} 舒淇影视作品`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="mt-5 max-w-sm mx-auto lg:mx-0 space-y-3">
              <button
                type="button"
                onClick={() => {
                  toggleFavorite(work.id);
                  showToast(favorited ? "已取消收藏" : "已添加收藏");
                }}
                className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  favorited
                    ? "bg-accent-100 text-accent-700 border border-accent-300"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={favorited ? "ri-heart-fill" : "ri-heart-line"} />
                {favorited ? "已收藏" : "收藏该作品"}
              </button>
              <p className="text-xs text-secondary-500 text-center">
                收藏仅保存在本机浏览器，用于演示，无需登录。
              </p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-3">
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs">
                {work.type}
              </span>
              <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                {work.genre}
              </span>
              <span className="flex items-center gap-1 text-sm text-foreground-600">
                <i className="ri-star-fill text-accent-500" />
                {work.rating.toFixed(1)}
              </span>
            </div>

            <h2 className="mt-4 font-heading text-xl md:text-2xl font-semibold text-foreground-950">
              {work.enTitle}
            </h2>

            <dl className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div>
                <dt className="text-xs text-secondary-500">上映年份</dt>
                <dd className="mt-1 font-heading text-base text-foreground-950">
                  {work.year}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-secondary-500">影片类型</dt>
                <dd className="mt-1 font-heading text-base text-foreground-950">
                  {work.genre} · {work.type}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-secondary-500">导演</dt>
                <dd className="mt-1 font-heading text-base text-foreground-950">
                  {work.director}
                </dd>
              </div>
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-xs text-secondary-500">舒淇饰演</dt>
                <dd className="mt-1 font-heading text-base text-foreground-950">
                  {work.role}
                </dd>
              </div>
            </dl>

            {work.highlight && (
              <div className="mt-5 inline-flex items-center gap-2 rounded-md bg-accent-50 border border-accent-200 px-4 py-3 text-sm text-accent-700">
                <i className="ri-award-line text-lg" />
                {work.highlight}
              </div>
            )}

            <div className="mt-8">
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                剧情简介
              </h3>
              <span className="block h-px w-10 bg-accent-400 mt-3 mb-5" />
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                {work.synopsis}
              </p>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="text-sm text-secondary-500">所属分类：</span>
              <Link
                to="/works"
                className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs hover:bg-background-200 transition-colors cursor-pointer"
              >
                {work.genre}
              </Link>
              <Link
                to={`/works?type=${work.type}`}
                className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs hover:bg-background-200 transition-colors cursor-pointer"
              >
                {work.type}
              </Link>
              <Link
                to="/works"
                className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs hover:bg-background-200 transition-colors cursor-pointer"
              >
                {Math.floor(work.year / 10) * 10} 年代
              </Link>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-background-100 border-t border-background-200">
          <div className="max-w-content mx-auto px-4 md:px-8 py-14 md:py-20">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
              相关推荐
            </h2>
            <span className="block h-px w-12 bg-accent-400 mt-4 mb-8" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {related.map((item) => (
                <WorkCard key={item.id} work={item} />
              ))}
            </div>
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[80] animate-fade-up">
          <div className="flex items-center gap-2 px-5 py-3 rounded-full bg-foreground-950 text-background-50 text-sm">
            <i className="ri-checkbox-circle-line text-accent-400 text-lg" />
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}